import { renderMarketingPage, SUPPORT_EMAIL } from './shared.js';

// ─────────────────────────────────────────────────────────────────────────
// Public "For schools" page (#/schools) — explains the seat-licence
// reseller model to school/college decision-makers, mirroring what
// functions/schools.js actually implements:
//   • the school prepays a wallet and buys student SEATS at a wholesale
//     per-seat/term price;
//   • it charges its own students whatever it likes, entirely off-platform
//     (the platform never sets or sees that price);
//   • while the licence is active, linked students' viewing is covered;
//     content creation by students is off by default and billed to the
//     school wallet if the school switches it on;
//   • teachers get a monthly free-generation quota and don't use a seat;
//   • the school gets its own dashboard (seats, term, wallet, teachers).
// Prices/quotas are admin-configurable server-side, so this page keeps
// numbers out of the copy and points schools at the contact channel.
// ─────────────────────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  {
    icon: '🚀',
    title: '1. Start your free trial',
    text: 'Sign in, verify a mobile number, and your school is live in seconds with its own code and dashboard — a free 3-month, viewing-only trial. No admin step, no card.',
  },
  {
    icon: '🔗',
    title: '2. Students link your code',
    text: 'Each student enters your school code once in their settings. During the trial their viewing, the Library, and every free animation are covered — no cards, no individual subscriptions.',
  },
  {
    icon: '🎟️',
    title: '3. Buy seats when you\'re ready',
    text: 'Top up your school wallet online and buy exactly as many student seats as you need at a simple wholesale per-seat, per-term price — renew or resize any time, all self-serve.',
  },
  {
    icon: '💡',
    title: '4. You set your own price',
    text: 'You\'re free to offer Simustage to your students at whatever price fits your community — that relationship stays entirely between you and them.',
  },
];

const BENEFITS = [
  {
    icon: '📺',
    title: 'Viewing covered by the seat',
    text: 'While your seat licence is active, every linked student can view assigned and purchased content with no personal wallet and no monthly charge of their own.',
  },
  {
    icon: '🎛️',
    title: 'You control creation',
    text: 'Student content creation is off by default. Flip one switch and students can build their own simulations, billed transparently to your school wallet — never to the student.',
  },
  {
    icon: '👩‍🏫',
    title: 'Teacher accounts included',
    text: 'Designate teachers who get a generous free monthly creation quota, never consume a student seat, and can build and assign content to your students.',
  },
  {
    icon: '📊',
    title: 'Your own dashboard',
    text: 'Seats licensed and in use, term dates, wallet balance, and a full ledger of every rupee — visible only to your school\'s own account.',
  },
  {
    icon: '🔒',
    title: 'Nothing is ever deleted',
    text: 'If a term lapses, student access simply pauses with a "renew" message until you top up — every course, simulation, and purchase stays exactly where it was.',
  },
  {
    icon: '🧾',
    title: 'Predictable spend',
    text: 'One prepaid wallet funds everything — seats and (if you enable it) student creation. No surprise invoices, no per-student billing sprawl.',
  },
];

const FAQS = [
  {
    q: 'How much does a seat cost?',
    a: 'Seats are sold at a flat wholesale price per student, per term. Contact us for current pricing — and remember the price you offer your own students is entirely up to you.',
  },
  {
    q: 'Do our students need their own wallets or payment details?',
    a: 'No. A linked student\'s viewing is covered by your seat licence, and if you enable content creation it\'s billed to the school wallet — a student\'s own wallet is never touched for school-sponsored activity.',
  },
  {
    q: 'What happens when our term ends?',
    a: 'Nothing is deleted. Students see a gentle "ask your school to renew" message instead of their content, and access resumes the moment you renew. Renewing before the term ends extends from your current end date, so you never lose paid days.',
  },
  {
    q: 'Can we change how many seats we have?',
    a: 'Yes — buying again resets your seat count and extends your term. The only rule: you can\'t buy fewer seats than you currently have linked students, so nobody is stranded.',
  },
  {
    q: 'How do teachers fit in?',
    a: 'Your school owner account can designate any number of teacher accounts. Teachers don\'t use a student seat, get a free monthly creation quota, and beyond it their creation is billed to the school wallet like a student\'s.',
  },
  {
    q: 'How does the free trial work?',
    a: 'Start it yourself in seconds — sign in, verify a mobile number, and your school goes live with a 3-month, viewing-only trial covering up to a set number of students. Student content-creation stays off during the trial; turn it on any time by adding funds and buying seats.',
  },
  {
    q: 'How do we top up the school wallet?',
    a: `Top up online yourself from your school dashboard — pay by card or UPI and your balance is available immediately. Prefer a custom plan or invoice? Write to ${SUPPORT_EMAIL}.`,
  },
];

export function renderSchools(container, signedIn) {
  const body = `
    <main class="mkt-main">
      <section class="mkt-page-hero">
        <span class="pill">For schools &amp; colleges</span>
        <h1>Bring interactive, build-it-yourself learning to your whole school.</h1>
        <p>One prepaid licence covers all your students. You buy seats at a simple wholesale price, set your own price for your community, and keep full control — no per-student billing, no payment details from families.</p>
        <div class="mkt-hero-actions" style="margin-top:22px;">
          <a href="#/school/start" class="btn btn-primary btn-lg">Start your free 3-month trial</a>
          <a href="#/school/start" class="btn btn-lg">School owner sign in</a>
        </div>
      </section>

      <section class="mkt-section">
        <div class="mkt-section-head">
          <h2>How it works</h2>
          <p>From first conversation to students learning — four steps.</p>
        </div>
        <div class="feature-grid">
          ${HOW_IT_WORKS.map((s) => `
            <div class="feature-card">
              <div class="feature-icon">${s.icon}</div>
              <h3>${s.title}</h3>
              <p>${s.text}</p>
            </div>
          `).join('')}
        </div>
      </section>

      <section class="mkt-section mkt-section-alt">
        <div class="mkt-section-head">
          <h2>What your school gets</h2>
        </div>
        <div class="feature-grid">
          ${BENEFITS.map((b) => `
            <div class="feature-card">
              <div class="feature-icon">${b.icon}</div>
              <h3>${b.title}</h3>
              <p>${b.text}</p>
            </div>
          `).join('')}
        </div>
      </section>

      <section class="mkt-section">
        <div class="mkt-section-head">
          <h2>Questions schools ask us</h2>
        </div>
        <div class="faq-list">
          ${FAQS.map((f) => `
            <details class="faq-item">
              <summary>${f.q}</summary>
              <p>${f.a}</p>
            </details>
          `).join('')}
        </div>
      </section>

      <section class="mkt-cta-band">
        <div class="mkt-cta-inner">
          <h2>Ready to bring Simustage to your students?</h2>
          <p>Start your free 3-month trial now — it takes less than a minute.</p>
          <a href="#/school/start" class="btn btn-primary btn-lg">Start your free trial</a>
        </div>
      </section>
    </main>
  `;
  renderMarketingPage(container, '/schools', signedIn, body);
}
